import React, { useRef, useCallback, useEffect } from 'react';
import { useKeyboard } from './KeyboardContext';
import getIcon from '../../utils/Icon';

const NumericKeyboard = () => {
    const { handleKeyPress, closeKeyboard, pasteFromClipboard } = useKeyboard();

    // Ускоряющийся backspace
    const bsTimer = useRef(null);
    const bsCount = useRef(0);

    const scheduleNextBackspace = useCallback(() => {
        bsCount.current++;
        const count = bsCount.current;
        let delay;
        if (count < 3) delay = 150;
        else if (count < 6) delay = 100;
        else if (count < 10) delay = 70;
        else if (count < 18) delay = 50;
        else if (count < 30) delay = 35;
        else delay = 25;
        
        bsTimer.current = setTimeout(() => {
            handleKeyPress('BACKSPACE');
            scheduleNextBackspace();
        }, delay);
    }, [handleKeyPress]);

    const startBackspaceRepeat = useCallback(() => {
        bsCount.current = 0;
        bsTimer.current = setTimeout(() => {
            handleKeyPress('BACKSPACE');
            scheduleNextBackspace();
        }, 400);
    }, [handleKeyPress, scheduleNextBackspace]);

    const stopBackspaceRepeat = useCallback(() => {
        if (bsTimer.current) {
            clearTimeout(bsTimer.current);
            bsTimer.current = null;
        }
        bsCount.current = 0;
    }, []);

    useEffect(() => {
        return () => stopBackspaceRepeat();
    }, [stopBackspaceRepeat]);

    const keys = [
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
        ['PASTE', '0', 'BACKSPACE'],
    ];

    const renderKey = (key) => {
        const isBackspace = key === 'BACKSPACE';
        const isPaste = key === 'PASTE';
        const isDot = key === '.';

        // Backspace: long-press support
        if (isBackspace) {
            return (
                <button
                    key={key}
                    className="flex-1 h-[56px] rounded-[12px] flex items-center justify-center
                        text-white font-medium
                        active:scale-[0.97] transition-all duration-100"
                    style={{
                        background: 'linear-gradient(180deg, #3A3A3E 0%, #2A2A2E 100%)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.05)',
                    }}
                    onTouchStart={() => { handleKeyPress('BACKSPACE'); startBackspaceRepeat(); }}
                    onTouchEnd={stopBackspaceRepeat}
                    onTouchCancel={stopBackspaceRepeat}
                    onMouseDown={() => { handleKeyPress('BACKSPACE'); startBackspaceRepeat(); }}
                    onMouseUp={stopBackspaceRepeat}
                    onMouseLeave={stopBackspaceRepeat}
                >
                    <svg width="28" height="22" viewBox="0 0 28 20" fill="none">
                        <path 
                            d="M8.5 2L2 10L8.5 18H26V2H8.5Z" 
                            stroke="#EAEAEA" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                        <path 
                            d="M12 7L19 13M19 7L12 13" 
                            stroke="#EAEAEA" 
                            strokeWidth="2" 
                            strokeLinecap="round"
                        />
                    </svg>
                </button>
            );
        }

        // Paste button
        if (isPaste) {
            return (
                <button
                    key={key}
                    onClick={pasteFromClipboard}
                    className="flex-1 h-[56px] rounded-[12px] flex items-center justify-center gap-1.5
                        text-white font-medium
                        active:scale-[0.97] transition-all duration-100"
                    style={{
                        background: 'linear-gradient(180deg, #3A3A3E 0%, #2A2A2E 100%)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.05)',
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path 
                            d="M16 4H18C19.1046 4 20 4.89543 20 6V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V6C4 4.89543 4.89543 4 6 4H8" 
                            stroke="#EAEAEA" 
                            strokeWidth="1.5" 
                            strokeLinecap="round"
                        />
                        <rect x="8" y="2" width="8" height="4" rx="1" stroke="#EAEAEA" strokeWidth="1.5"/>
                    </svg>
                    <span className="text-white text-[11px]">.</span>
                </button>
            );
        }

        return (
            <button
                key={key}
                onClick={() => handleKeyPress(key)}
                className="flex-1 h-[56px] rounded-[12px] flex items-center justify-center
                    text-white font-medium
                    active:scale-[0.97] transition-all duration-100"
                style={{
                    background: 'linear-gradient(180deg, #333336 0%, #252528 100%)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.05)',
                }}
            >
                <span className="text-[24px]">{key}</span>
            </button>
        );
    };

    return (
        <div className="w-full px-3 pb-14">
            {/* Keys grid */}
            <div className="flex flex-col gap-[8px]">
                {keys.map((row, rowIndex) => (
                    <div key={rowIndex} className="flex flex-row gap-[8px]">
                        {row.map(renderKey)}
                    </div>
                ))}
            </div>
            
            {/* Bottom row: dot + Apply */}
            <div className="flex flex-row gap-[8px] mt-3">
                <button
                    onClick={() => handleKeyPress('.')}
                    className="w-[80px] h-[52px] rounded-[14px] flex items-center justify-center
                        active:scale-[0.98] transition-all duration-100"
                    style={{
                        background: 'linear-gradient(180deg, #3A3A3E 0%, #2A2A2E 100%)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(234, 234, 234, 0.05)',
                    }}
                >
                    <span className="text-white text-[24px] font-medium">.</span>
                </button>
                
                <button
                    onClick={closeKeyboard}
                    className="flex-1 h-[52px] rounded-[14px] flex items-center justify-center gap-2.5
                        active:scale-[0.98] transition-all duration-100"
                    style={{
                        background: '#2B8FF4',
                        boxShadow: '0 4px 16px rgba(43, 143, 244, 0.3)',
                    }}
                >
                    <span className="text-white text-[17px] font-bold tracking-wide">Apply</span>
                </button>
            </div>
        </div>
    );
};

export default NumericKeyboard;
